import {dedupingMixin} from '@polymer/polymer/lib/utils/mixin';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';
import {
  Chart,
  PointElement,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Filler,
  Legend,
  Title,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {parseInputColor} from '../../javascripts/modules/colors.js';

Chart.register(PointElement,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Filler,
  Legend,
  Title,
  Tooltip);

/**
 * @polymer
 * @mixinFunction
 **/
export const KeliChartBehavior = dedupingMixin((superClass) => class extends ResizeObserverBehavior(superClass) {
  static get properties() {
    return {
      caption: {type: String, scopevar: true, observer: 'captionChanged'},
      chartData: {type: Object, observer: 'chartDataChanged'},
      chartOptions: Object,
      height: {type: Number, scopevar: true, observer: 'heightChanged'},
      labelKeys: {type: Array, scopevar: true, observer: 'labelKeysChanged'},
      needsDataLoaded: {type: Boolean, value: true},
      options: {type: Object, scopevar: true, observer: 'optionsChanged'},
      valueKeys: {type: Array, scopevar: true, observer: 'valueKeysChanged'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli('keliChartElementActivated');
  }

  connectedCallback() {
    super.connectedCallback();

    this.chartOptions = this.chartOptions || {};
    const defaultOptions = {
      maintainAspectRatio: !this.height,
      onClick: this.handleChartClick.bind(this),
      spanGaps: true,
      plugins: {
        title: {
          display: !!this.caption,
          text: this.caption || ''
        },
        legend: {
          display: false
        }
      },
      scales: {
        x: {},
        y: {
          beginAtZero: true
        }
      },
      animation: false
    };

    this.chart = new Chart(this.$.chartElement, {
      type: this.chartType || 'bar',
      options: Object.assign(defaultOptions, this.chartOptions, this.options || {}),
    });

    this.dispatchEvent(new CustomEvent('panelElementCreated', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  drawChart() {
    if (!this.chartData) {
      return;
    }

    const labels = [];
    const dataSets = [];

    if (this.valueKeys) {
      this.valueKeys.forEach(function (options) {
        let borderColor = options.color || 'a700';
        let backgroundColor = options.background || options.color || 'a700';
        if (Array.isArray(borderColor)) {
          borderColor.forEach(function (color, index) {
            borderColor[index] = parseInputColor(color)
          }, this);
        } else {
          borderColor = parseInputColor(borderColor);
        }
        if (Array.isArray(backgroundColor)) {
          backgroundColor.forEach(function (color, index) {
            backgroundColor[index] = parseInputColor(color)
          }, this);
        } else {
          backgroundColor = parseInputColor(backgroundColor);
        }

        dataSets.push({
          name: options.name,
          label: options.caption || '',
          borderColor,
          backgroundColor,
          data: []
        });
      }, this);
    }

    for (const row of this.chartData) {
      dataSets.forEach(set => {
        set.data.push(row[set.name])
      }, this);

      if (this.labelKeys) {
        labels.push(row[this.labelKeys[0].name]);
      }
    }

    this.chart.config.data.datasets = dataSets;
    this.chart.config.data.labels = labels;

    this.chart.update();
  }

  chartDataChanged() {
    this.drawChart();
  }

  optionsChanged(newValue) {
    if (newValue) {
      Object.assign(this.chart.options, newValue);
      this.chart.update();
    }
  }

  valueKeysChanged() {
    this.drawChart();
  }

  labelKeysChanged() {
    this.drawChart();
  }

  captionChanged(newValue) {
    if (this.chart) {
      this.chart.options.plugins.title = {
        display: !!newValue,
        text: newValue
      };
      this.chart.update();
    }
  }

  heightChanged(newValue) {
    const container = this.$.chartElement.parentNode;
    const outerContainer = this.$.outerChartContainer;

    if (newValue && typeof newValue === 'string' && newValue.endsWith('%')) {
      if (container) {
        container.style.height = newValue;
      }
      if (outerContainer) {
        outerContainer.style.height = newValue;
      }
    } else {
      if (container) {
        container.style.height = `${newValue}px`;
      }
    }
    this.$.chartElement.setAttribute('height', newValue);
    if (this.chart) {
      this.chart.options.maintainAspectRatio = false;
      this.chart.resize();
    }
  }

  handleChartClick(event) {
    const elementsAtEvent = this.chart.getElementsAtEventForMode(event, 'nearest', {intersect: true}, false);
    let row;
    if (elementsAtEvent && elementsAtEvent.length) {
      row = this.chartData[elementsAtEvent[0].index];
    }
    if (row) {
      this.dispatchEvent(new CustomEvent('keliChartElementActivated', {detail: {rowid: row.id, data: row}}));
    }
  }

  handleElementResized() {
    if (this.chart) {
      this.chart.resize();
    }
  }
});